import React, { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Smartphone } from "lucide-react";
import { enablePush, disablePush, pushStatus, pushSupport, testPush } from "../lib/push";

/**
 * One control that turns push notifications on for this device.
 * `token` is whichever session owns the notifications - the CRM token on the
 * admin panel, the mailbox token inside the webmail.
 */
export default function PushToggle({ token, compact }) {
  const [state, setState] = useState({ loading: true, on: false, devices: 0, configured: false });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const sup = pushSupport();

  const refresh = useCallback(async () => {
    try {
      const s = await pushStatus(token);
      let on = false;
      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration("/");
        const sub = reg && (await reg.pushManager?.getSubscription());
        on = !!sub && typeof Notification !== "undefined" && Notification.permission === "granted";
      }
      setState({ loading: false, on, devices: s.devices, configured: s.configured });
    } catch {
      setState((v) => ({ ...v, loading: false }));
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const turnOn = async () => {
    setBusy(true); setErr(""); setMsg("");
    try { await enablePush(token); setMsg("Notifications are on for this device."); await refresh(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const turnOff = async () => {
    setBusy(true); setErr(""); setMsg("");
    try { await disablePush(token); setMsg("Notifications are off for this device."); await refresh(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const sendTest = async () => {
    setBusy(true); setErr(""); setMsg("");
    try { const r = await testPush(token); setMsg(`Sent to ${r.delivered} device${r.delivered === 1 ? "" : "s"}.`); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (state.loading) return <Loader2 size={16} className="animate-spin text-slate-400" />;

  return (
    <div className={compact ? "" : "rounded-xl border border-slate-200 dark:border-slate-800 p-4"} data-testid="push-toggle">
      {!compact && (
        <div className="flex items-center gap-2 mb-1">
          <Bell size={16} className="text-indigo-600 dark:text-indigo-400" />
          <h3 className="font-semibold text-slate-900 dark:text-white text-sm">Push notifications</h3>
        </div>
      )}
      {!compact && (
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
          New mail, appointment reminders and deposits, delivered to this device even when the app is closed.
        </p>
      )}

      {!state.configured && (
        <p className="text-xs text-amber-700 dark:text-amber-400 mb-2" data-testid="push-unconfigured">
          The server has no VAPID keys yet — generate them with “Set up push” in the launcher.
        </p>
      )}

      {!sup.ok ? (
        <p className="text-xs text-slate-500 dark:text-slate-400 flex items-start gap-2" data-testid="push-unsupported">
          <Smartphone size={14} className="mt-0.5 shrink-0" /> {sup.why}
        </p>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={state.on ? turnOff : turnOn} disabled={busy || !state.configured} data-testid="push-btn"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50 ${
              state.on ? "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                       : "bg-indigo-600 hover:bg-indigo-700 text-white"}`}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : state.on ? <BellOff size={13} /> : <Bell size={13} />}
            {state.on ? "Turn off" : "Turn on"}
          </button>
          {state.on && (
            <button onClick={sendTest} disabled={busy} data-testid="push-test"
              className="px-3 py-1.5 rounded-lg text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
              Send a test
            </button>
          )}
          {state.devices > 0 && (
            <span className="text-xs text-slate-400">{state.devices} device{state.devices === 1 ? "" : "s"} registered</span>
          )}
        </div>
      )}

      {msg && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2">{msg}</p>}
      {err && <p className="text-xs text-rose-600 dark:text-rose-400 mt-2" role="alert">{err}</p>}
    </div>
  );
}
